import { Injectable } from '@angular/core';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { DisableDebitWelcomeQueryInterface } from '@zaoblako/shared/modules/saas/dtos/disable.debit.welcome.query.dto';
import type { FindTenantInterface } from '@zaoblako/shared/modules/saas/dtos/find.tenant.dto';
import type { PersonalAccountTransactionListInterface } from '@zaoblako/shared/modules/saas/dtos/personal-account/personal-account.transaction.list.dto';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { TenantPageInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-page.dto';
import type { TenantAnalyticsQueryInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-analytics/tenant-analytics-query.dto';
import type { TenantAnalyticsSectionInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-analytics/tenant-analytics-section.dto';
import type { TenantStatisticsFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-statistics/tenant.statistics.find.dto';
import type { TenantSimpleFormInterface } from '@zaoblako/shared/modules/saas/dtos/tenant.simple.form.dto';
import type { TenantAvailableSubdomainsInterface } from '@zaoblako/shared/modules/saas/dtos/subdomain/tenant-available-subdomains.dto';
import type { TenantFormInterface } from '@zaoblako/shared/modules/saas/dtos/tenant/tenant-form.dto';
import type { UserLoginResponseInterface } from '@zaoblako/shared/modules/security/dtos/user/user-login-response.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TenantService extends BaseService {
  protected baseUrl = '/basis/saas/tenant';

  public findAll(queryParams: FindTenantInterface): Observable<TenantPageInterface> {
    return this.http.get<TenantPageInterface>(this.baseUrl, { params: this.objectToHttpParams(queryParams) });
  }

  public getCurrent(): Observable<TenantFindInterface> {
    return this.http.get<TenantFindInterface>(`${this.baseUrl}/current`);
  }

  public setCurrent(body: TenantFormInterface): Observable<TenantFindInterface> {
    return this.http.put<TenantFindInterface>(`${this.baseUrl}/current`, body);
  }

  public copyFromDemo(): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/copy-from-demo`, {});
  }

  public disableWelcome(): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/disable-welcome-without-revert`);
  }

  public reminderWelcome(): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/disable-welcome`);
  }

  public createSimple(body: TenantSimpleFormInterface): Observable<TenantFindInterface> {
    return this.http.post<TenantFindInterface>(`${this.baseUrl}/simple`, body);
  }

  public disableDebitWelcome(queryParams?: DisableDebitWelcomeQueryInterface): Observable<void> {
    return this.http.get<void>(`${this.baseUrl}/disable-debit-welcome`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getSubdomains(): Observable<TenantAvailableSubdomainsInterface> {
    return this.http.get<TenantAvailableSubdomainsInterface>(`${this.baseUrl}/subdomains`);
  }

  public getActiveUsers(tenantId: string): Observable<UserLoginResponseInterface[]> {
    return this.http.get<UserLoginResponseInterface[]>(`${this.baseUrl}/${tenantId}/active-users`);
  }

  public getHistory(id: string, queryParams: PaginationQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getTransactionHistory(
    id: string,
    queryParams: PaginationRequiredQueryInterface
  ): Observable<PersonalAccountTransactionListInterface> {
    return this.http.get<PersonalAccountTransactionListInterface>(`${this.baseUrl}/${id}/transactions`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public getStatistics(id: string): Observable<TenantStatisticsFindInterface> {
    return this.http.get<TenantStatisticsFindInterface>(`${this.baseUrl}/${id}/statistics`);
  }

  public getAnalytics(id: string, queryParams: TenantAnalyticsQueryInterface): Observable<TenantAnalyticsSectionInterface[]> {
    return this.http.get<TenantAnalyticsSectionInterface[]>(`${this.baseUrl}/${id}/analytics`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public restoreById(id: string): Observable<TenantFindInterface> {
    return this.http.patch<TenantFindInterface>(`${this.baseUrl}/${id}`, null);
  }

  public isConnectedToAtrium(): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/current/is-connected-to-atrium`);
  }
}
